import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import Reveal from "../components/Reveal";
import usePageMeta from "../hooks/usePageMeta";

const FOTOS = [
    {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_benefits-platform-2/artifacts/z4a3yvvn__095.webp",
        alt: "Equipe da Smart Benefícios trabalhando no escritório",
        classe: "left-0 top-[2%] w-[62%] aspect-[4/3] -rotate-3 z-10",
        delay: 0,
    },
    {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_benefits-platform-2/artifacts/7zr3t5yn__092.webp",
        alt: "Servolo Tobias e equipe reunidos em reunião no escritório",
        classe: "right-0 top-0 w-[47%] aspect-[4/3] rotate-[2.5deg] z-20",
        delay: 0.12,
    },
    {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_benefits-platform-2/artifacts/p5yb9j3q__090.webp",
        alt: "Equipe atenta durante apresentação no escritório da Smart",
        classe: "left-[4%] bottom-[3%] w-[55%] aspect-[4/3] rotate-[1.5deg] z-30",
        delay: 0.24,
    },
    {
        src: "https://customer-assets-rejwkqb3.emergentagent.net/job_benefits-platform-2/artifacts/vo0p6oxi__052.webp",
        alt: "Servolo Tobias apresentando ao time na sede da empresa",
        classe: "right-[2%] bottom-[8%] w-[41%] aspect-[3/4] -rotate-2 z-40",
        delay: 0.36,
    },
];

function CollagemFotos() {
    return (
        <div className="relative mx-auto w-full max-w-[560px]" data-testid="quem-somos-colagem">
            <svg className="absolute -left-8 top-8 h-28 w-28 text-azul-200" aria-hidden="true">
                <defs>
                    <pattern id="dots-colagem" width="14" height="14" patternUnits="userSpaceOnUse">
                        <circle cx="2" cy="2" r="2" fill="currentColor" />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#dots-colagem)" />
            </svg>
            <div aria-hidden="true" className="absolute -right-6 -top-6 h-28 w-28 rounded-full border-[3px] border-dashed border-laranja-300" />

            <div className="relative aspect-[1/1.12] w-full sm:aspect-[1/1.05]">
                {FOTOS.map((f, i) => (
                    <motion.figure
                        key={f.src}
                        initial={{ opacity: 0, y: 36, scale: 0.94 }}
                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.8, delay: f.delay, ease: [0.22, 1, 0.36, 1] }}
                        whileHover={{ scale: 1.03, rotate: 0, zIndex: 50 }}
                        className={`absolute overflow-hidden rounded-2xl border-[5px] border-white shadow-[0_24px_48px_-16px_rgba(6,37,90,0.35)] ${f.classe}`}
                        data-testid={`colagem-foto-${i + 1}`}
                    >
                        <img src={f.src} alt={f.alt} loading="lazy" className="h-full w-full object-cover" />
                    </motion.figure>
                ))}

                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                    className="absolute -left-2 bottom-[-4%] z-50 rotate-[-3deg] rounded-2xl bg-laranja-500 px-5 py-3 text-white shadow-laranja"
                    data-testid="colagem-selo"
                >
                    <p className="font-display text-2xl font-extrabold leading-none">2024</p>
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-wider opacity-90">ano em que a Smart nasceu</p>
                </motion.div>
            </div>
        </div>
    );
}

export default function QuemSomos() {
    usePageMeta(
        "Quem Somos | Smart Benefícios",
        "A Smart Benefícios nasceu em 2024, a partir de quase 20 anos de experiência de Servolo Tobias no mercado de benefícios, especialmente na gestão de Vale-Transporte."
    );

    return (
        <>
            <section className="relative overflow-hidden bg-azul-50/70 pb-16 pt-[128px] md:pb-24 md:pt-[168px]" data-testid="quem-somos-hero">
                <div aria-hidden="true" className="pointer-events-none absolute -top-24 left-[-8%] h-[420px] w-[420px] rounded-full bg-laranja-100/50 blur-3xl" />
                <div className="container-x relative z-10 grid items-center gap-14 lg:grid-cols-2 lg:gap-10">
                    <div>
                        <Reveal>
                            <span className="inline-flex items-center gap-2 rounded-full border border-azul-100 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-azul-700">
                                <span className="h-1.5 w-1.5 rounded-full bg-laranja-500" />
                                Quem Somos
                            </span>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-tinta sm:text-5xl lg:text-[3.4rem]" data-testid="quem-somos-title">
                                Experiência que entende o mercado. Tecnologia que transforma a gestão.
                            </h1>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <p className="mt-6 text-base leading-relaxed text-tinta-medio sm:text-lg" data-testid="quem-somos-texto">
                                A Smart Benefícios nasceu em <strong className="text-azul-700">2024</strong>, a partir de <strong className="text-azul-700">quase 20 anos de experiência</strong> de <strong className="text-azul-700">Servolo Tobias</strong> no mercado de benefícios, especialmente na gestão de Vale-Transporte.
                            </p>
                        </Reveal>
                        <Reveal delay={0.22}>
                            <p className="mt-4 text-base leading-relaxed text-tinta-medio sm:text-lg">
                                Fundador da <strong className="text-azul-700">VT Service</strong>, Servolo construiu uma trajetória de reconhecimento no setor, com a empresa figurando por duas vezes entre as cinco marcas mais lembradas do segmento no Brasil e, em <strong className="text-azul-700">2019</strong>, entre as melhores empresas fornecedoras do setor.
                            </p>
                        </Reveal>
                        <Reveal delay={0.26}>
                            <p className="mt-4 text-base leading-relaxed text-tinta-medio sm:text-lg">
                                Hoje, essa experiência está na Smart Benefícios, que une conhecimento de mercado e tecnologia para tornar a gestão de benefícios mais simples, eficiente e acessível para empresas e colaboradores.
                            </p>
                        </Reveal>
                        <Reveal delay={0.32}>
                            <Link
                                to="/contato"
                                data-testid="quem-somos-cta"
                                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-azul-500 px-7 py-3.5 text-base font-bold text-white shadow-[0_10px_28px_-8px_rgba(13,82,206,0.55)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-azul-600"
                            >
                                Fale com um especialista
                                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                            </Link>
                        </Reveal>
                    </div>

                    <Reveal delay={0.1} className="lg:pl-6">
                        <CollagemFotos />
                    </Reveal>
                </div>
            </section>

            {/* Citação */}
            <section className="bg-white py-20 md:py-28" data-testid="quem-somos-frase">
                <div className="container-x">
                    <Reveal>
                        <div className="mx-auto max-w-4xl rounded-[2.5rem] border border-azul-100 bg-azul-50/70 p-10 text-center md:p-16" data-testid="quem-somos-quote-card">
                            <Quote className="mx-auto h-8 w-8 text-laranja-500" />
                            <p className="mt-6 font-display text-2xl font-extrabold leading-snug tracking-tight text-azul-900 sm:text-3xl lg:text-4xl">
                                "Experiência para entender. Tecnologia para simplificar."
                            </p>
                        </div>
                    </Reveal>

                    <div className="mt-14 grid gap-6 sm:grid-cols-3" data-testid="quem-somos-fatos">
                        {[
                            { valor: "Quase 20 anos", texto: "de experiência de Servolo Tobias no mercado de benefícios" },
                            { valor: "3 soluções", texto: "Vale-Transporte, Vale-Refeição e Vale-Alimentação em uma única plataforma" },
                            { valor: "R$ 99/mês", texto: "planos a partir de, para simplificar a gestão de benefícios" },
                        ].map((f, i) => (
                            <Reveal key={f.valor} delay={i * 0.08} className="h-full">
                                <div className="h-full rounded-3xl border border-slate-200/80 bg-white p-7 text-center shadow-suave transition-all duration-300 hover:-translate-y-1 hover:shadow-card">
                                    <p className="font-display text-2xl font-extrabold tracking-tight text-azul-700">{f.valor}</p>
                                    <p className="mt-2 text-sm leading-relaxed text-tinta-suave">{f.texto}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
